/* global axios */
// Usa o axios GLOBAL do dashboard (window.axios = createAxios), que carrega o
// interceptor de autenticação — o import cru de 'axios' não leva o token e dá 401.
//
// Webphone BCVOZ (PABX Bravotech) carregado GLOBALMENTE: o SDK vem do CDN
// (jsDelivr, sempre a última versão) e o widget flutuante fica visível em todas
// as páginas, como a BCVOZ recomendou. A sessão (vxToken + extensão SIP) é
// montada pelo nosso backend a partir do token da conta salvo na caixa.
//
// PRÓXIMA ETAPA: sessão POR AGENTE logado (hoje o backend usa o ramal primário
// da conta) e o formato oficial de sessão que a BCVOZ vai enviar.

let carregado = false; // injeta o SDK uma vez só por carregamento de página
const RESOLVED_URL =
  'https://data.jsdelivr.com/v1/packages/npm/@bcvoz/webphone/resolved';

async function buscarSessao(accountId) {
  const url = `/api/v1/accounts/${accountId}/kanban/bcvoz_tokens/webphone_session`;
  const { data } = await axios.get(url);
  return data?.data || null; // { api_base, session } ou null
}

async function injetarSdk() {
  // fetch nativo (não o axios do dashboard): é cross-origin pro CDN e não deve
  // levar o interceptor de auth nem baseURL.
  let version;
  try {
    const r = await fetch(RESOLVED_URL, { cache: 'no-store' });
    version = (await r.json())?.version;
  } catch (e) {
    version = null;
  }
  const src = version
    ? `https://cdn.jsdelivr.net/npm/@bcvoz/webphone@${version}/dist/bcvoz.umd.js`
    : 'https://cdn.jsdelivr.net/npm/@bcvoz/webphone/dist/bcvoz.umd.js';
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('BCVoz: falha ao carregar do CDN'));
    document.head.appendChild(s);
  });
}

export function useBcvozWebphone() {
  // Carrega e inicializa o webphone global. Idempotente e silencioso: se o
  // módulo bcvoz não está ligado ou não há sessão, não faz nada.
  const loadBcvozWebphone = async accountId => {
    if (carregado || !accountId || typeof window === 'undefined') return;

    let cfg;
    try {
      cfg = await buscarSessao(accountId);
    } catch (e) {
      return; // sem endpoint/integração → silencioso
    }
    // Sem integração ativa → não carrega. COM integração mas SEM sessão (o agente
    // não casou com um usuário BCVOZ) → carrega o widget SEM logar (login manual).
    if (!cfg?.api_base) return;

    // A sessão vem do POST /api/pabx/login: o token de sessão é `pabxToken`.
    // O SDK espera `vxToken` — mapeia um no outro quando necessário.
    const session = cfg.session
      ? {
          ...cfg.session,
          vxToken: cfg.session.vxToken || cfg.session.pabxToken,
        }
      : null;

    carregado = true;
    try {
      if (!window.BCVoz) await injetarSdk();
      // Integração OFICIAL da BCVOZ (documentacao/playground.html): o webphone roda em
      // `srcdoc` (NÃO 'hosted') e o REGISTRO vem de `setAuth({ vxToken })` — NÃO de passar
      // a sessão inteira no init(). Passar a `session` com a `extension` dependia da
      // entrega via bridge e NÃO registrava; com `setAuth({vxToken})` o SDK busca a
      // extensão sozinho (/api/pabx/extension) e registra. `apiBase` é necessário porque,
      // ao contrário do playground (que roda na própria origem da BCVOZ), aqui o webphone
      // roda no cwmanual e precisa saber onde fica a API.
      window.BCVoz.init({
        apiBase: cfg.api_base,
        mode: 'srcdoc',
        open: false, // começa INVISÍVEL (sem janela)
        launcher: false, // SEM aba/botão flutuante — o widget só aparece em ligação
        position: 'bottom-right',
      });
      // Sem botão flutuante: o webphone abre SOZINHO quando há ligação — entrante
      // (call:incoming) ou ao discar pelo ticket (call:dialing, disparado por
      // BCVoz.call()). Fora de ligação, fica invisível. O agente fecha a janela
      // normalmente quando quiser.
      const abrirWebphone = () => {
        try {
          window.BCVoz.show();
        } catch (err) {
          /* widget ainda subindo — o próximo evento resolve */
        }
      };
      window.BCVoz.on('call:incoming', abrirWebphone);
      window.BCVoz.on('call:dialing', abrirWebphone);
      // LOGIN + REGISTRO (com match de e-mail→ramal): o registro vem de
      // `setAuth({ vxToken })` — o jeito oficial da BCVOZ. Como usamos `launcher:false`
      // + `open:false`, o iframe do webphone só nasce ao abrir; então damos um show()
      // pra MONTAR, o setAuth REGISTRA (o SDK busca a extensão e registra no gateway), e
      // hide() volta a esconder — o registro segue em 2º plano. Assim o ramal fica
      // registrado e PRONTO PRA RECEBER, sem a TAG flutuante, e a janela só aparece na
      // ligação (call:incoming/dialing → show()).
      if (session?.vxToken) {
        const entrar = () => {
          try {
            window.BCVoz.show();
            // Sessão COMPLETA (com `extension`), não só { vxToken }: o setAuth só com o
            // vxToken NÃO leva as credenciais SIP → o webphone loga "extension NOT found"
            // e não registra. Com a sessão inteira, o SDK faz o `addExtension` (via ponte)
            // e `bravophoneWebphoneLoggedIn` vira true → registra o SIP.
            window.BCVoz.setAuth(session);
            window.BCVoz.hide();
          } catch (err) {
            /* ponte do iframe ainda subindo — a retentativa resolve */
          }
        };
        entrar();
        setTimeout(entrar, 1500);
      }
      // SEM match: o webphone guarda a última sessão em storage e volta logado
      // sozinho. Força o logout pra cair na TELA DE LOGIN do próprio widget.
      if (!session && typeof window.BCVoz.logout === 'function') {
        const forcarLogout = () => {
          try {
            window.BCVoz.logout();
          } catch (err) {
            /* bridge ainda subindo — a 2ª tentativa resolve */
          }
        };
        forcarLogout();
        setTimeout(forcarLogout, 1500); // garante após a ponte do iframe subir
      }
    } catch (e) {
      carregado = false; // deixa tentar de novo numa próxima navegação
      // eslint-disable-next-line no-console
      console.error('[BCVOZ webphone]', e?.message || e);
    }
  };

  return { loadBcvozWebphone };
}
